import { Injectable } from '@angular/core';
import { RoutesForLane } from './lane.routes';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { EntitySaveOptions, SafeAny } from '@pf/shared-common';
import { map, Observable, of } from 'rxjs';
import { SchnFunctionsDataService } from '../infrastructure/schn-functions-data.service';
import {
    LaneCreate,
    LaneDto,
    LaneMapper,
    LaneRead,
    LaneSearchParams,
    LaneStore,
} from '@pf/platform-loads/domain';
import { asArraySafe } from '@pf/shared-utility';
import { LaneViewModelBase } from '@control-tower/platform-loads';
import { RadiusLaneDataService } from '../infrastructure/radius-lane.data.service';

function filterLanesByItem(
    lanes: LaneDto[],
    itemId: string
): { lanes: LaneDto[]; isRequestedCommodity: boolean } {
    const filteredLanes = lanes.filter(lane => {
        return asArraySafe(lane.rates).some(
            laneRate => !laneRate.isDeleted && laneRate.description === itemId
        );
    });
    if (filteredLanes.length > 0) {
        return { lanes: filteredLanes, isRequestedCommodity: false };
    }

    const filteredLanesByRequestedCommodity = lanes.filter(lane => {
        return asArraySafe(lane.rates).some(
            laneRate =>
                !laneRate.isDeleted &&
                laneRate.customFields?.some(
                    customField =>
                        customField?.key === 'Requested Commodity' &&
                        customField?.value &&
                        customField?.value.indexOf(itemId) > -1
                )
        );
    });
    return {
        lanes: filteredLanesByRequestedCommodity,
        isRequestedCommodity: true,
    };
}

@Injectable()
export class LaneFacade extends AbstractManageEntityFacade<
    LaneDto,
    LaneRead,
    LaneCreate,
    LaneSearchParams
> {
    nameField: keyof LaneDto = 'name';

    constructor(
        dataService: RadiusLaneDataService,
        routes: RoutesForLane,
        store: LaneStore,
        mapper: LaneMapper,
        private functionsService: SchnFunctionsDataService
    ) {
        super(dataService, routes, store, mapper);
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny // TODO
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof LaneDto, string>> {
        return { name: searchText };
    }

    checkIfLaneExists(dto: LaneDto) {
        return dto.id
            ? of(false)
            : this.oneTimeSearch$({
                  pageSize: 1,
                  pageIndex: 1,
                  filters: {
                      originLocation: dto.originLocation?.id,
                      destinationLocation: dto.destinationLocation?.id,
                  },
              }).pipe(map(result => result.totalRecords > 0));
    }

    override save$(
        dto: LaneDto,
        options?: EntitySaveOptions<LaneDto>
    ): Observable<LaneDto> {
        return super.save$(dto, {
            ...options,
            postSave: savedDto => {
                // Marcus - Bit of a hack, but we need to update the origin and destination locations in the store to populate the table
                const entity = this.store.get(savedDto.id);
                if (entity) {
                    entity.originLocation = dto.originLocation as SafeAny;
                    entity.destinationLocation =
                        dto.destinationLocation as SafeAny;
                    this.store.update(entity);
                }
            },
        });
    }

    runAutomationsForEntity$(laneId: string) {
        return this.functionsService.runAutomationsForLane$(laneId);
    }

    getByOriginCommodityAndDestination(
        originLocationId: string,
        itemId: string,
        vendorLocationId: string
    ) {
        return this.searchAll$({
            pageSize: 50,
            pageIndex: 1,
            filters: {
                originLocation: originLocationId,
                destinationLocation: vendorLocationId,
                search: itemId,
            },
        }).pipe(
            map(result => {
                return filterLanesByItem(result, itemId);
            })
        );
    }

    getByOriginAndCommodity(originLocationId: string, itemId: string) {
        return this.searchAll$({
            pageSize: 50,
            pageIndex: 1,
            filters: {
                originLocation: originLocationId,
                search: itemId,
            },
        }).pipe(
            map(result => {
                return filterLanesByItem(result, itemId);
            })
        );
    }

    override applyResourceAuth(body: LaneViewModelBase): void {
        body.resourceAuth = { allAuthorized: true };
    }
}
